import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import {
  useGetLeadsByWorkshopQuery,
  useStatusUpdateMutation,
} from "../../../Services/leads/LeadGeneration";
import { toast } from "react-toastify";
import { formatToIST } from "../../../components/reusableComponent/DateFormat";

const Leads = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const workshopId = localStorage.getItem("workshop_id");

  // Helper function to get date string in YYYY-MM-DD format
  const getDateString = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  // Get default dates (1 month range)
  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    return {
      start: getDateString(startDate),
      end: getDateString(endDate),
    };
  };

  const defaultDates = getDefaultDates();

  // Filter states - Changed default to "all" for UI
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [status, setStatus] = useState("all");

  // Get today's date in YYYY-MM-DD format for max date validation
  const today = getDateString(new Date());

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, status]);

  // Convert "all" to empty string for API
  const getApiStatus = (statusValue: string) => {
    return statusValue === "all" ? "" : statusValue;
  };

  // Reset filters to default
  const handleResetFilters = () => {
    setStartDate(defaultDates.start);
    setEndDate(defaultDates.end);
    setStatus("all");
    setPage(1);
  };

  // lead data api
  const {
    data: LeadGenerationData,
    isFetching,
    refetch,
  } = useGetLeadsByWorkshopQuery(
    {
      page,
      page_size: pageSize,
      workshopId,
      start_date: startDate,
      end_date: endDate,
      status: getApiStatus(status),
    },
    {
      skip: !workshopId,
    },
  );

  const [triggerUpdateStatus, { isLoading: isUpdating }] =
    useStatusUpdateMutation();

  // Export to CSV
  const handleExportCSV = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.loading("Preparing CSV export...");

      // Build query parameters
      const queryParams = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      // Only add status if it's not "all"
      if (getApiStatus(status)) {
        queryParams.append("status", getApiStatus(status));
      }

      // Get API base URL dynamically
      const fetchingTenant = window.location.hostname.split(".")[0];
      const apiPort = process.env.REACT_APP_API_PROTOCOL;
      const API_BASE_URL = `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc`;

      const response = await fetch(
        `${API_BASE_URL}/leads/export/?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            // Remove Content-Type header for GET request
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.statusText}`);
      }

      // Check the content type to determine file format
      const contentType = response.headers.get("content-type");
      const contentDisposition = response.headers.get("content-disposition");

      // Get the blob
      const blob = await response.blob();

      // Validate blob
      if (blob.size === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Determine file extension based on content type
      let fileExtension = "csv";
      let fileName = `leads_export_${new Date().toISOString().split("T")[0]}`;

      if (
        contentType?.includes("spreadsheet") ||
        contentType?.includes("excel")
      ) {
        fileExtension = "xlsx";
      } else if (contentDisposition) {
        // Try to extract filename from content-disposition header
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          contentDisposition,
        );
        if (matches && matches[1]) {
          const suggestedFileName = matches[1].replace(/['"]/g, "");
          fileName = suggestedFileName.split(".")[0];
          fileExtension = suggestedFileName.split(".").pop() || "csv";
        }
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.${fileExtension}`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      toast.success(`File exported successfully!`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export file. Please try again.");
    }
  };

  // ALTERNATIVE: If you want to convert the leads data to CSV on the frontend
  // This approach doesn't rely on the backend API

  const handleExportCSVFromData = () => {
    try {
      if (!leads || leads.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Define CSV headers
      const headers = [
        "Sr.No.",
        "Lead ID",
        "Name",
        "Email",
        "Mobile",
        "State",
        "City",
        "Status",
        "Workshop",
      ];

      // Convert leads data to CSV rows
      const rows = leads.map((lead, index) => [
        index + 1,
        lead.lead_id || "-",
        lead.name || "-",
        lead.email || "-",
        lead.mobile || "-",
        lead.state || "-",
        lead.city || "-",
        formatStatusLabel(lead.status),
        lead.workshop_name || "-",
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((cell) => {
              // Escape cells that contain commas, quotes, or newlines
              const cellStr = String(cell);
              if (
                cellStr.includes(",") ||
                cellStr.includes('"') ||
                cellStr.includes("\n")
              ) {
                return `"${cellStr.replace(/"/g, '""')}"`;
              }
              return cellStr;
            })
            .join(","),
        ),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `leads_export_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export CSV. Please try again.");
    }
  };

  // Handle status update - Open confirmation modal
  const handleStatusUpdate = async (leadId: string) => {
    setSelectedLeadId(leadId);
    setConfirmationOpen(true);
  };

  // Confirm and execute status update
  const handleConfirmStatusUpdate = async () => {
    if (!selectedLeadId) return;

    try {
      const payload = {
        lead_id: selectedLeadId,
      };

      const response = await triggerUpdateStatus({ body: payload }).unwrap();
      refetch();

      // Show success message from API response
      if (response?.success && response?.message) {
        toast.success(response.message);
      } else {
        toast.success("Status updated successfully!");
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
    } finally {
      setConfirmationOpen(false);
      setSelectedLeadId(null);
    }
  };

  // Cancel status update
  const handleCancelStatusUpdate = () => {
    setConfirmationOpen(false);
    setSelectedLeadId(null);
  };

  useEffect(() => {
    if (isFetching) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isFetching]);

  // Update leads and total count when data changes
  useEffect(() => {
    if (LeadGenerationData) {
      // Extract leads from the nested leads.results structure
      const leadsData = LeadGenerationData.leads?.results || [];

      setLeads(leadsData);
      setTotalCount(LeadGenerationData.leads?.count || 0);
    }
  }, [LeadGenerationData]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const getStatusColor = (status: string | undefined) => {
    const statusLower = status?.toLowerCase() || "pending";
    switch (statusLower) {
      case "pending":
        return { bgcolor: "#FFF3CD", color: "#856404" };
      case "forwarded":
        return { bgcolor: "#D4EDDA", color: "#155724" };
      case "contacted":
        return { bgcolor: "#D1ECF1", color: "#0C5460" };
      case "followed_up":
        return { bgcolor: "#CCE5FF", color: "#004085" };
      default:
        return { bgcolor: "#F0F0F0", color: "#333" };
    }
  };

  const formatStatusLabel = (status: string | undefined) => {
    if (!status) return "Pending";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.3)",
          zIndex: 9999,
        }}
      >
        <Typography sx={{ color: "#ff6b35", fontFamily: "Karbon" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Karbon",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Box
        className="d-flex justify-content-between align-items-center"
        sx={{
          bgcolor: "#fff",
          px: 4,
          py: 3,
          borderBottom: "1px solid #f0e5e0",
        }}
      >
        <Typography variant="h5" color="#666666" fontFamily="Karbon">
          Lead Generation Management
        </Typography>

        <Button
          variant="contained"
          onClick={handleExportCSV}
          sx={{
            bgcolor: "#ff6b35",
            color: "#fff",
            fontFamily: "Karbon",
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(255,107,53,0.3)",
            "&:hover": {
              bgcolor: "#e55a2b",
            },
          }}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box sx={{ px: 4, py: 2 }}>
          {/* Filter Section */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: "16px",
              border: "1px solid #e0e0e0",
              bgcolor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Typography
              variant="h6"
              color="#333"
              fontFamily="Karbon"
              mb={3}
              fontWeight={600}
            >
              Filter Leads
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: 3,
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  color="#555"
                  fontFamily="Karbon"
                  fontSize="0.9rem"
                  mb={1}
                >
                  Start Date
                </Typography>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: endDate || today,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Karbon",
                      bgcolor: "#fafafa",
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#ff6b35",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff6b35",
                      },
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  color="#555"
                  fontFamily="Karbon"
                  fontSize="0.9rem"
                  mb={1}
                >
                  End Date
                </Typography>
                <TextField
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: startDate,
                    max: today,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Karbon",
                      bgcolor: "#fafafa",
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#ff6b35",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff6b35",
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  color="#555"
                  fontFamily="Karbon"
                  fontSize="0.9rem"
                  mb={1}
                >
                  Status
                </Typography>
                <TextField
                  select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  fullWidth
                  placeholder="Select Status"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Karbon",
                      bgcolor: "#fafafa",
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#ff6b35",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff6b35",
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="followed_up">Followed Up</MenuItem>
                  <MenuItem value="forwarded">Forwarded</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>

          {totalCount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography color="#666666" fontSize="1.1rem" fontFamily="Karbon">
                Total Leads: {totalCount}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              borderRadius: "12px",
              border: "1px solid #f0e5e0",
              overflow: "hidden",
              bgcolor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontFamily: "Karbon",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f7d3c5",
                    color: "#666666",
                  }}
                >
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "80px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Sr.No.
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "120px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Lead ID
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      minWidth: "150px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      minWidth: "200px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "130px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Mobile
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "140px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    State
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "120px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    City
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "120px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      minWidth: "200px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Workshop
                  </th>

                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      minWidth: "200px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Created At
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      fontWeight: 600,
                      width: "120px",
                      borderBottom: "2px solid #f0e5e0",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      style={{ textAlign: "center", padding: "60px 16px" }}
                    >
                      <Typography
                        color="#666666"
                        fontSize="1.1rem"
                        mb={2}
                        fontFamily="Karbon"
                      >
                        No leads available
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  leads?.map((lead, index) => (
                    <tr
                      key={lead.id}
                      style={{
                        backgroundColor:
                          index % 2 === 1 ? "#fff5f2" : "#ffffff",
                        transition: "background-color 0.2s",
                        borderBottom: "1px solid #f5f5f5",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff5f2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          index % 2 === 1 ? "#fff5f2" : "#ffffff";
                      }}
                    >
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Chip
                          label={(page - 1) * pageSize + index + 1}
                          size="small"
                          sx={{
                            bgcolor: "#f7d3c5",
                            color: "#ff6b35",
                            fontFamily: "Karbon",
                            fontWeight: 500,
                          }}
                        />
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography
                          color="#666666"
                          fontFamily="Karbon"
                          sx={{ fontWeight: 500 }}
                        >
                          {lead.lead_id || "-"}
                        </Typography>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.name}
                        </Typography>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography
                          color="#666666"
                          fontFamily="Karbon"
                          sx={{
                            wordBreak: "break-word",
                            fontSize: "0.9rem",
                          }}
                        >
                          {lead.email || "-"}
                        </Typography>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography
                          color="#666666"
                          fontFamily="Karbon"
                          sx={{ fontFamily: "karbon" }}
                        >
                          {lead.mobile}
                        </Typography>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.state || "-"}
                        </Typography>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.city || "-"}
                        </Typography>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Chip
                          label={formatStatusLabel(lead.status)}
                          size="small"
                          sx={{
                            ...getStatusColor(lead.status),
                            fontFamily: "Karbon",
                          }}
                        />
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <Typography
                          color="#666666"
                          fontFamily="Karbon"
                          sx={{
                            fontSize: "0.9rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {lead.workshop_name || "-"}
                        </Typography>
                      </td>

                      <td style={{ padding: "16px" }}>
                        <Typography
                          color="#666666"
                          fontFamily="Karbon"
                          fontSize="0.9rem"
                        >
                          {formatToIST(lead.created_at)}
                        </Typography>
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleStatusUpdate(lead.lead_id)}
                          disabled={isUpdating || lead.status === "followed_up"}
                          sx={{
                            bgcolor:
                              lead.status === "followed_up"
                                ? "#e0e0e0"
                                : "#ff6b35",
                            color: "#fff",
                            fontFamily: "Karbon",
                            textTransform: "none",
                            px: 2,
                            py: 0.5,
                            fontSize: "0.85rem",
                            borderRadius: "6px",
                            "&:hover": {
                              bgcolor:
                                lead.status === "followed_up"
                                  ? "#e0e0e0"
                                  : "#e55a2b",
                            },
                            "&:disabled": {
                              bgcolor: "#e0e0e0",
                              color: "#999",
                            },
                          }}
                        >
                          {lead.status === "followed_up"
                            ? "Followed Up"
                            : "Follow Up"}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>

          {/* Pagination Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              pt: 2,
              borderTop: "1px solid #f0e5e0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Karbon",
                  color: "#666666",
                  fontSize: "0.9rem",
                }}
              >
                Rows per page:
              </Typography>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{
                  fontFamily: "Karbon",
                  color: "#666666",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #f0e5e0",
                  outline: "none",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Karbon",
                  color: "#666666",
                  fontSize: "0.9rem",
                }}
              >
                {startIndex + 1} - {endIndex} of {totalCount}
              </Typography>

              <Box sx={{ display: "flex", gap: 0.5 }}>
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  style={{
                    padding: "6px 16px",
                    borderRadius: "6px",
                    border: "1px solid #ff6b35",
                    backgroundColor: page === 1 ? "#f5f5f5" : "#fff",
                    color: page === 1 ? "#ccc" : "#666666",
                    fontFamily: "Karbon",
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (page !== 1) {
                      e.currentTarget.style.backgroundColor = "#fff5f2";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (page !== 1) {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  Prev
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const firstPage = Math.max(
                    1,
                    Math.min(page - 2, totalPages - 4),
                  );
                  const pageNum = firstPage + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        minWidth: "36px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ff6b35",
                        backgroundColor: pageNum === page ? "#ff6b35" : "#fff",
                        color: pageNum === page ? "#fff" : "#666666",
                        fontFamily: "Karbon",
                        fontWeight: pageNum === page ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (pageNum !== page) {
                          e.currentTarget.style.backgroundColor = "#fff5f2";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (pageNum !== page) {
                          e.currentTarget.style.backgroundColor = "#fff";
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  style={{
                    padding: "6px 16px",
                    borderRadius: "6px",
                    border: "1px solid #ff6b35",
                    backgroundColor: page === totalPages ? "#f5f5f5" : "#fff",
                    color: page === totalPages ? "#ccc" : "#666666",
                    fontFamily: "Karbon",
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (page !== totalPages) {
                      e.currentTarget.style.backgroundColor = "#fff5f2";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (page !== totalPages) {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  Next
                </button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmationOpen}
        onClose={handleCancelStatusUpdate}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Karbon",
            fontSize: "1.3rem",
            fontWeight: 600,
            color: "#333",
            pb: 1,
          }}
        >
          Confirm Follow Up
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontFamily: "Karbon",
              fontSize: "1rem",
              color: "#666",
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to mark this lead as followed up? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <button
            className="btn btn-light"
            style={{ fontSize: "15px", fontWeight: 200, fontFamily: "karbon" }}
            onClick={handleCancelStatusUpdate}
          >
            Cancel
          </button>
          <Button
            variant="contained"
            onClick={handleConfirmStatusUpdate}
            sx={{
              bgcolor: "#ff6b35",
              color: "#fff",
              fontFamily: "Karbon",
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(255,107,53,0.3)",
              "&:hover": {
                bgcolor: "#e55a2b",
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leads;
